import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import type { WhatsAppSessionConfig } from '../../lib/hooks/useWhatsAppConfigs';
import { tip } from '@/components/messages/ChatTooltip';

interface SessionSelectorProps {
  configs: WhatsAppSessionConfig[];
  activeConfigId: string | null;
  onSelect: (configId: string) => void;
  onAddSession?: () => void;
}

function formatPhone(phone: string): string {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length > 10) {
    const cc = cleaned.slice(0, cleaned.length - 10);
    const rest = cleaned.slice(cleaned.length - 10);
    return `+${cc} ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
  }
  return phone;
}

function HealthDot({ status }: { status: string }) {
  const color = status === 'healthy' ? '#22C55E' : status === 'error' ? '#EF4444' : '#9CA3AF';
  const label = status === 'healthy'
    ? 'Collegato: i messaggi arrivano e partono'
    : status === 'error'
      ? 'Scollegato: va ricollegato da Impostazioni > WhatsApp'
      : 'Stato non ancora verificato';
  return (
    <span
      className="flex-shrink-0 inline-block w-2 h-2 rounded-full"
      style={{ backgroundColor: color }}
      role="img"
      aria-label={label}
      {...tip(label, 'right')}
    />
  );
}

/** Badge modalità numero (user-facing, mai "Cloud API"). WAHA = nessun badge. */
function ProviderTag({ serviceName }: { serviceName: WhatsAppSessionConfig['serviceName'] }) {
  if (serviceName === 'whatsapp_cloud') {
    return <span className="flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Ufficiale</span>;
  }
  if (serviceName === 'whatsapp_cloud_coex') {
    return <span className="flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Ufficiale + App</span>;
  }
  return null;
}

export function SessionSelector({ configs, activeConfigId, onSelect, onAddSession }: SessionSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Hide if only one config and no onAddSession
  if (configs.length <= 1 && !onAddSession) return null;

  const activeConfig = configs.find(c => c.id === activeConfigId) || configs[0];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-[#25292C] border border-[rgba(55,53,47,0.09)] dark:border-white/10 rounded-lg text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 transition-colors cursor-pointer"
      >
        {activeConfig && <HealthDot status={activeConfig.healthStatus} />}
        <span className="font-medium truncate max-w-[200px]">
          {activeConfig?.displayName || formatPhone(activeConfig?.phoneNumber || '')}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-[#787774] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 w-72 bg-white dark:bg-[#2F3437] rounded-lg shadow-xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 overflow-hidden">
          <div className="px-3 py-2 border-b border-[rgba(55,53,47,0.06)] dark:border-white/5">
            <span className="text-[11px] font-medium text-[#787774] uppercase tracking-wider">Sessioni WhatsApp</span>
          </div>
          <div className="py-1 max-h-64 overflow-y-auto">
            {configs.map(config => {
              const isActive = config.id === (activeConfigId || configs[0]?.id);
              return (
                <button
                  key={config.id}
                  onClick={() => {
                    onSelect(config.id);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                    isActive
                      ? 'bg-[rgba(55,53,47,0.04)] dark:bg-white/5'
                      : 'hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5'
                  }`}
                >
                  <HealthDot status={config.healthStatus} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-[#37352F] dark:text-white font-medium truncate">
                        {config.displayName || formatPhone(config.phoneNumber)}
                      </span>
                      <ProviderTag serviceName={config.serviceName} />
                    </div>
                    <div className="text-[11px] text-[#787774] truncate">
                      {config.displayName ? formatPhone(config.phoneNumber) + ' · ' : ''}
                      {config.visibility === 'global'
                        ? 'Condiviso con il team'
                        : `Personale${config.ownerName ? ` di ${config.ownerName}` : ''}`}
                    </div>
                  </div>
                  {isActive && (
                    <span className="text-[10px] font-medium text-[#E91E8C] bg-[#E91E8C]/10 px-1.5 py-0.5 rounded">Attiva</span>
                  )}
                </button>
              );
            })}
          </div>
          {onAddSession && (
            <>
              <div className="border-t border-[rgba(55,53,47,0.06)] dark:border-white/5" />
              <button
                onClick={() => {
                  setOpen(false);
                  onAddSession();
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 transition-colors"
              >
                <Plus className="h-3.5 w-3.5 text-[#787774]" />
                <span className="text-sm text-[#787774] font-medium">Aggiungi sessione</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
